import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { FiShare2, FiMail, FiLink } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { scrollToId } from '../utils/scroll';
import { useCart } from '../context/CartContext';

const ShareMenu = ({ video }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const shareUrl = `${window.location.origin}/#videos`;
  const shareText = `Check out "${video.title}" on Eunbro — ${video.description}`;

  const shareToWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
      '_blank',
      'noopener,noreferrer'
    );
    setOpen(false);
  };

  const shareToEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(video.title)}&body=${encodeURIComponent(
      `${shareText}\n\n${shareUrl}`
    )}`;
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Could not copy the link.');
    }
    setOpen(false);
  };

  return (
    <div className="relative flex-shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={`Share ${video.title}`}
        aria-expanded={open}
        className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
      >
        <FiShare2 className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-2 w-44 bg-white rounded-md shadow-lg border border-gray-100 py-1 z-30">
          <button
            type="button"
            onClick={shareToWhatsApp}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FaWhatsapp className="w-4 h-4 text-[#25D366]" />
            WhatsApp
          </button>
          <button
            type="button"
            onClick={shareToEmail}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FiMail className="w-4 h-4 text-gray-500" />
            Email
          </button>
          <button
            type="button"
            onClick={copyLink}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FiLink className="w-4 h-4 text-gray-500" />
            Copy Link
          </button>
        </div>
      )}
    </div>
  );
};

const VideoCard = ({ video, index, onAddToCart, added }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08, ease: 'easeOut' }}
      className="group relative flex-shrink-0 w-72 sm:w-80 snap-start bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-indigo-100"
    >
      {/* Video Container */}
      <div className="relative overflow-hidden pt-[100%] bg-gray-100">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center animate-pulse bg-gray-200">
            <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        )}
        <video
          src={video.src}
          autoPlay
          loop
          muted
          playsInline
          onCanPlay={() => setLoaded(true)}
          className={`absolute top-0 left-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

        {video.bestSeller && (
          <span className="absolute top-3 left-3 z-20 bg-yellow-400 text-gray-900 text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md">
            BESTSELLER
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-base font-bold text-gray-900 mb-1 truncate">
          {video.title}
        </h3>
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">
          {video.description}
        </p>

        <div className="flex items-center justify-between mb-3">
          <span className="text-lg font-bold text-indigo-600">
            {video.price}
          </span>
          <div className="flex items-center" aria-label={`Rated ${video.rating} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((star) => (
              <svg
                key={star}
                aria-hidden="true"
                className={`w-3.5 h-3.5 ${star <= video.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <ShareMenu video={video} />

          <button
            type="button"
            onClick={() => onAddToCart(video)}
            aria-pressed={added}
            className={`flex-1 text-sm font-semibold py-2.5 px-4 rounded-none transition-colors duration-300 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 ${
              added
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-transparent text-gray-900 hover:bg-gray-900 hover:text-white'
            }`}
          >
            {added ? (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Added to Cart
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
                Add to Cart
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const FeaturedVideos = () => {
  const { addItem, isInCart } = useCart();
  const scrollerRef = useRef(null);

  const handleAddToCart = (video) => {
    if (isInCart(video.id)) return;
    addItem(video);
    toast.success(`${video.title} added to cart!`);
  };

  const scrollByAmount = (amount) => {
    scrollerRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const videos = [
    {
      id: 1,
      title: 'Engaging Promo Clip',
      description: 'Boost your brand with a 30-second animated promo perfect for social media campaigns.',
      src: '/2.mov',
      price: '$5',
      bestSeller: true,
      rating: 4,
    },
    {
      id: 2,
      title: 'Vivid Explainer Video',
      description: 'Simplify your message with a concise animated explainer for your website or pitch.',
      src: '/3.mov',
      price: '$5',
      bestSeller: false,
      rating: 5,
    },
    {
      id: 3,
      title: 'Dynamic Ad Animation',
      description: 'Capture attention with a vibrant, short ad for your next marketing campaign.',
      src: '/4.mov',
      price: '$5',
      bestSeller: true,
      rating: 4,
    },
    {
      id: 4,
      title: 'Playful Brand Intro',
      description: 'A fun, energetic intro animation to open your videos and presentations.',
      src: '/1.mov',
      price: '$5',
      bestSeller: false,
      rating: 5,
    },
    {
      id: 5,
      title: 'Story-Driven Explainer',
      description: 'A narrative-style animation that walks viewers through your product story.',
      src: '/2.mov',
      price: '$5',
      bestSeller: false,
      rating: 4,
    },
    {
      id: 6,
      title: 'Social Media Teaser',
      description: 'A punchy, attention-grabbing teaser built for Instagram and TikTok feeds.',
      src: '/3.mov',
      price: '$5',
      bestSeller: true,
      rating: 5,
    },
    {
      id: 7,
      title: 'Product Showcase Reel',
      description: 'Highlight your product\'s best features in one vibrant animated reel.',
      src: '/4.mov',
      price: '$5',
      bestSeller: false,
      rating: 4,
    },
  ];

  return (
    <section id="videos" className="py-20 bg-gradient-to-b from-gray-50 to-white scroll-mt-20">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <span className="inline-block bg-indigo-100 text-indigo-800 text-sm font-semibold px-4 py-1 rounded-full mb-4">
            Premium Collection
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Shop Our <span className="text-indigo-600">Premium</span> Animated Videos
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Discover short, high-quality animated videos crafted to elevate your brand, perfect for social media, websites, and advertising.
          </p>
        </motion.div>
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => scrollByAmount(-340)}
          aria-label="Scroll left"
          className="hidden sm:flex absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center text-gray-600 hover:text-indigo-600 hover:shadow-xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div
          ref={scrollerRef}
          className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-4 px-6 lg:px-8 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {videos.map((video, index) => (
            <VideoCard
              key={video.id}
              video={video}
              index={index}
              onAddToCart={handleAddToCart}
              added={isInCart(video.id)}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => scrollByAmount(340)}
          aria-label="Scroll right"
          className="hidden sm:flex absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center text-gray-600 hover:text-indigo-600 hover:shadow-xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mt-12">
          <button
            type="button"
            onClick={() => scrollToId('contact')}
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
          >
            Request a Custom Animation
            <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedVideos;
